import type { EntityRootData } from '@web-config-app/core';

/**
 * Factory that creates a transformer to remove translations for locales
 * that are not included in the tenant's allowed locale codes.
 *
 * Usage (later, when wiring up use-entity-create):
 *   const transformer = createTranslationsFilterTransformer(['en-US', 'fr-CA']);
 *   useEntityCreate({ options: { transformers: [transformer] } })
 */
export const createTranslationsFilterTransformer = (
  allowedLocaleCodes: ReadonlyArray<string>,
) => {
  const allowed = new Set(allowedLocaleCodes);

  return (data: EntityRootData): EntityRootData => {
    if (!data?.attributes) {
      return data;
    }
    const translations = data?.attributes?.translations;

    // Nothing to do if there are no translations or it's not a plain object
    if (
      !translations ||
      typeof translations !== 'object' ||
      Array.isArray(translations)
    ) {
      return data;
    }

    const entries = Object.entries(translations);
    const allowedEntries = entries.filter(([localeCode]) =>
      allowed.has(localeCode),
    );

    // If everything is already allowed, return original object to preserve referential equality
    if (allowedEntries.length === entries.length) {
      return data;
    }

    // Shallow-copy data and attributes to maintain immutability while avoiding deep clone
    const next: EntityRootData = { ...(data || {}) };
    next.attributes = { ...data.attributes };

    if (allowedEntries.length === 0) {
      // Remove translations entirely when no allowed locales remain
      if ('translations' in next.attributes) {
        delete next.attributes.translations;
      }
      return next;
    }

    // Set filtered translations
    next.attributes.translations = Object.fromEntries(allowedEntries);

    return next;
  };
};
